import _ from 'underscore';

import SequenceAttributeCollection from 'components/SequenceAttributeCollection';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {PolymerFeatureAttributes, PolymerFeatureModel} from 'constants/SequenceConstants';

import SequenceRegion from './SequenceRegion';

export interface TranslationAttributes extends PolymerFeatureAttributes {
  id: number | string;
  translationGroupId: number | string | null;
  // Need to constrain since translations cannot have a strand value of 0.
  strand: StrandOfDoubleStranded;
}
interface Translation {
  get<K extends keyof TranslationAttributes>(key: K): TranslationAttributes[K];
  set(obj: Partial<TranslationAttributes>): Translation;
  set<K extends keyof TranslationAttributes>(key: K, value: TranslationAttributes[K]): Translation;
  fastSet(obj: Partial<TranslationAttributes>): Translation;
  fastSet<K extends keyof TranslationAttributes>(key: K, value: TranslationAttributes[K]): Translation;
  fastUnset<K extends keyof TranslationAttributes>(key: K): Translation;
  // Part modification code hackily tacks on these booleans sometimes.
  isDirectStart?: boolean;
  isDirectEnd?: boolean;
}

class Translation extends SequenceRegion implements PolymerFeatureModel {
  defaults(): Partial<TranslationAttributes> {
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(super.defaults(), {strand: 1});
  }

  strand(): StrandOfDoubleStranded {
    return this.get('strand');
  }
  translationGroupId(): number | string | null {
    return this.get('translationGroupId');
  }
  allowFullReplace(): boolean {
    return true;
  }
  /**
   * Return an identity string for the purpose of determining when a translation is the duplicate
   * of another.
   */
  identityFingerprint(): string {
    return `${this.start()}:${this.end()}:${this.strand()}`;
  }
}

export class TranslationCollection extends SequenceAttributeCollection<Translation> {
  static initClass(): void {
    this.prototype.model = Translation; // should match component/translations.js
    this.prototype.attributeName = 'translations';
  }

  batchName(): string {
    return 'translations';
  }

  deepClone(offset: number = 0): TranslationCollection {
    const col = new TranslationCollection();
    this.each((t) => col.add(t.clone(offset)));
    return col;
  }
}

TranslationCollection.initClass();

export {Translation, Translation as Model, TranslationCollection as Collection};
